import { getPlatformTermsContent, type PlatformTermsVariant } from '../constants/platformTermsContent';
import { useI18n } from '../i18n';
import { PlatformTermsBody } from './PlatformTermsBody';
import styles from './PlatformTermsAcceptSection.module.css';

export type PlatformTermsAcceptSectionProps = {
  idPrefix: string;
  open: boolean;
  agreed: boolean;
  theme?: 'dark' | 'light';
  disabled?: boolean;
  termsVariant?: PlatformTermsVariant;
  onToggleOpen: () => void;
  onAgreedChange: (agreed: boolean) => void;
};

export function PlatformTermsAcceptSection({
  idPrefix,
  open,
  agreed,
  theme = 'light',
  disabled = false,
  termsVariant = 'default',
  onToggleOpen,
  onAgreedChange,
}: PlatformTermsAcceptSectionProps) {
  const { language, t } = useI18n();
  const content = getPlatformTermsContent(language, termsVariant);
  const isDark = theme === 'dark';

  return (
    <section
      className={`${styles.section} ${isDark ? styles.sectionDark : styles.sectionLight}`}
      aria-labelledby={`${idPrefix}-terms-heading`}
    >
      <button
        type="button"
        id={`${idPrefix}-terms-heading`}
        className={`${styles.toggle} ${isDark ? styles.toggleDark : styles.toggleLight}`}
        aria-expanded={open}
        onClick={onToggleOpen}
        disabled={disabled}
      >
        <svg
          className={`${styles.caret} ${isDark ? styles.caretDark : styles.caretLight}${
            open ? ` ${styles.caretOpen}` : ''
          }`}
          viewBox="0 0 16 16"
          width={16}
          height={16}
          aria-hidden
        >
          <path d="M6 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.75" />
        </svg>
        <span>{content.heading}</span>
      </button>
      {open ? (
        <div
          className={`${styles.body} ${isDark ? styles.bodyDark : styles.bodyLight}`}
          id={`${idPrefix}-terms-body`}
        >
          <PlatformTermsBody content={content} />
        </div>
      ) : null}
      <label
        className={`${styles.agreeLabel} ${isDark ? styles.agreeLabelDark : styles.agreeLabelLight}`}
      >
        <input
          type="checkbox"
          className={styles.agreeCheckbox}
          checked={agreed}
          disabled={disabled}
          onChange={(event) => onAgreedChange(event.target.checked)}
        />
        <span>{t('platform.termsAgree')}</span>
      </label>
    </section>
  );
}
